
import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Instagram } from 'lucide-react';
import Header from '@/components/Header.jsx';
import Footer from '@/components/Footer.jsx';
import FloatingWidgets from '@/components/FloatingWidgets.jsx';
import { SpotifyIcon, TikTokIcon, YouTubeIcon } from '@/components/SocialIcons.jsx';
import StatCard from '@/components/StatCard.jsx';
import CourseCard from '@/components/CourseCard.jsx';
import TestimonialsSection from '@/components/TestimonialsSection.jsx';
import NewsletterSection from '@/components/NewsletterSection.jsx';
import HomeNewsletterPopup from '@/components/HomeNewsletterPopup.jsx';
import { COURSE_IMAGES } from '@/data/courseImages.js';
import { getCourseBySlug } from '@/data/courseCatalog.js';

const podcastSpotifyUrl = 'https://open.spotify.com/show/3HquQ8JUS2jD4agvfLLuCF';
const podcastYoutubeUrl = 'https://www.youtube.com/@LuPistiner';
const socialImages = ['1-redes.webp', '2-redes.webp', '3-redes.webp', '4-redes.webp'];
const heroStats = [
  { number: '+25.000', label: 'Familias confiaron en mis cursos online' },
  { number: '+12.000', label: 'En nuestras consultorías personalizadas' },
  { number: '2019', label: 'Transformando el descanso de las familiasa ' }
];

function PodcastHomeSection() {
  return (
    <section className="w-full overflow-x-clip bg-[#fff0eb]">
      <div className="bg-primary pb-20 md:pb-24">
        <div className="mx-auto max-w-7xl px-4 pt-6 sm:px-6 md:pt-8 lg:px-8">
          <div className="relative mx-auto overflow-visible rounded-[1.75rem] bg-cover bg-center px-5 pb-14 pt-10 text-center text-white shadow-xl md:px-12 md:pb-20 md:pt-16 lg:pb-24 lg:pt-20" style={{ backgroundImage: "linear-gradient(rgba(45,18,12,.64),rgba(45,18,12,.72)),url('/fondopodcast-home.webp')" }}>
            <h2 className="mx-auto mb-5 text-6xl text-white md:mb-6 md:text-8xl">Sueño Sagrado</h2>
            <div className="mx-auto max-w-4xl text-lg font-bold leading-relaxed text-white drop-shadow-[0_2px_6px_rgba(0,0,0,0.85)] md:text-xl">
              <p className="mx-auto max-w-none">
                En este canal comparto conversaciones con madres y padres sobre el sueño infantil, la crianza real y todo eso de lo que poco se habla. Un espacio para acompañarte, compartir experiencias y recordarte que no estás sola.
              </p>
            </div>
            <div className="relative z-10 mx-auto -mb-[4.5rem] mt-8 flex max-w-5xl translate-y-8 flex-col items-center justify-center gap-4 sm:flex-row md:-mb-20 md:translate-y-10">
              <a href={podcastSpotifyUrl} target="_blank" rel="noreferrer" className="mx-auto flex w-fit max-w-full flex-none items-center gap-3 rounded-xl bg-[#ffe4dc] px-4 py-3 text-left text-primary shadow-lg hover:-translate-y-1 hover:bg-white sm:mx-0 sm:gap-4 sm:px-6 sm:py-4">
                <SpotifyIcon className="h-8 w-8 shrink-0 text-[#1ED760] sm:h-10 sm:w-10" />
                <span className="whitespace-nowrap font-rocky-extra-regular text-base leading-none sm:hidden">Escucha mi podcast en Spotify</span>
                <span className="hidden font-rocky-extra-regular leading-none sm:inline sm:whitespace-nowrap sm:text-xl md:text-2xl">Escuchá mi podcast Sueño Sagrado en Spotify</span>
              </a>
              <a href={podcastYoutubeUrl} target="_blank" rel="noreferrer" className="mx-auto flex w-fit max-w-full flex-none items-center gap-3 rounded-xl bg-[#ffe4dc] px-4 py-3 text-left text-primary shadow-lg hover:-translate-y-1 hover:bg-white sm:mx-0 sm:gap-4 sm:px-6 sm:py-4">
                <YouTubeIcon className="h-8 w-8 shrink-0 text-[#FF0000] sm:h-10 sm:w-10" />
                <span className="whitespace-nowrap font-rocky-extra-regular text-base leading-none sm:hidden">Escucha mi podcast en Youtube</span>
                <span className="hidden font-rocky-extra-regular leading-none sm:inline sm:whitespace-nowrap sm:text-xl md:text-2xl">Escuchá mi podcast Sueño Sagrado en YouTube</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function SocialHomeSection() {
  return (
    <section
      className="relative overflow-hidden bg-[#fff4ef] bg-cover bg-center px-4 py-12 sm:px-6 md:py-14 lg:px-8"
      style={{ backgroundImage: "url('/fondoredesociales.webp')" }}
    >
      <div className="relative mx-auto max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <h2 className="font-rocky-light mb-6 text-[2.25rem] font-light italic leading-tight text-[#a9432b] min-[380px]:text-[2.65rem] md:mb-9 md:text-6xl lg:text-7xl">
            Seguí aprendiendo conmigo
          </h2>

          <div className="relative mx-auto mb-7 h-[46rem] max-w-[23rem] min-[390px]:h-[49rem] sm:h-[50rem] sm:max-w-[34rem] lg:h-[32rem] lg:max-w-[62rem] xl:h-[34rem] xl:max-w-[70rem]">
            <img
              src={`/${socialImages[1]}`}
              alt="Contenido de TikTok de Lu Pistiner"
              loading="lazy"
              decoding="async"
              className="absolute left-[12%] top-[2%] z-10 h-[46%] w-auto max-w-none rotate-[-6deg] sm:left-[24%] sm:h-[48%] lg:left-[17%] lg:top-[-5%] lg:h-[110%] lg:rotate-0 xl:left-[18%]"
            />
            <div className="absolute right-[5%] top-[49%] z-20 h-[47%] rotate-[10deg] sm:right-[19%] sm:top-[48%] sm:h-[48%] lg:right-[17%] lg:top-[-5%] lg:h-[110%] lg:rotate-0 xl:right-[18%]">
              <img
                src={`/${socialImages[2]}`}
                alt="Contenido de TikTok de Lu Pistiner"
                loading="lazy"
                decoding="async"
                className="h-full w-auto max-w-none"
              />
            </div>
            <img
              src={`/${socialImages[0]}`}
              alt="Publicación de Instagram de Lu Pistiner"
              loading="lazy"
              decoding="async"
              className="absolute left-0 top-[42%] z-30 w-[60%] sm:left-[4%] sm:top-[43%] sm:w-[47%] lg:left-[-1%] lg:top-1/2 lg:w-[29%] lg:-translate-y-1/2 xl:left-0 xl:w-[28%]"
            />
            <img
              src={`/${socialImages[3]}`}
              alt="Publicación de Instagram de Lu Pistiner"
              loading="lazy"
              decoding="async"
              className="absolute right-0 top-[17%] z-30 w-[60%] sm:right-[3%] sm:top-[18%] sm:w-[47%] lg:right-[-1%] lg:top-1/2 lg:w-[29%] lg:-translate-y-1/2 xl:right-0 xl:w-[28%]"
            />
          </div>

          <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
            <a
              href="https://www.instagram.com/lu.pistiner/"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-base inline-flex min-h-12 items-center gap-3 bg-[#d8896d] px-4 text-lg text-white hover:bg-[#c97b61] sm:px-5 sm:text-xl"
            >
              <Instagram className="h-6 w-6" />
              Ir a Instagram
            </a>
            <a
              href="https://www.tiktok.com/@lu.pistiner"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-base inline-flex min-h-12 items-center gap-3 bg-[#a9432b] px-4 text-lg text-white hover:bg-primary sm:px-5 sm:text-xl"
            >
              <TikTokIcon className="h-5 w-5 text-white" />
              Ir a TikTok
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

const featuredCourses = [
  { slug: '0-4-meses', title: 'Sueño en bebés de 0 a 4 meses' },
  { slug: '4-20-meses', title: 'Sueño en bebés de 4 a 20 meses' },
  { slug: 'destete-nocturno', title: 'Destete Nocturno' },
].map(({ slug, title }) => {
  const course = getCourseBySlug(slug);
  return {
    image: course.image,
    title,
    priceARS: course.arsLabel,
    priceUSD: course.usdLabel,
    link: course.path,
  };
});

function HomePage() {
  return (
    <>
      <Header />
      <FloatingWidgets />
      <HomeNewsletterPopup />

      <main>
        {/* Hero Section */}
        <section className="bg-background pb-0 lg:pb-20">
          <div className="max-w-7xl mx-auto px-0 sm:px-6 lg:px-8">
            <div className="relative grid grid-cols-1 overflow-hidden sm:rounded-xl lg:grid-cols-2 lg:items-center lg:gap-12 lg:overflow-visible">
              <motion.div
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55 }}
                className="relative order-1 lg:order-1"
              >
                <img 
                  src="/FOTOS%20LUCILA%20WEB/LUCILA-HOME-1.jpg" 
                  alt="Consultoría en sueño infantil" 
                  loading="eager" 
                  decoding="async"
                  fetchPriority="high"
                  sizes="(min-width: 1024px) 50vw, 100vw"
                  className="aspect-[4/3] w-full object-cover object-center shadow-lg sm:rounded-xl lg:aspect-[4/5] lg:rounded-xl xl:aspect-square" 
                />
              </motion.div>

              <motion.div 
                initial={{ opacity: 0, x: 30 }} 
                animate={{ opacity: 1, x: 0 }} 
                transition={{ duration: 0.6, delay: 0.2 }} 
                className="relative z-10 order-2 px-6 pb-9 pt-8 lg:px-0 lg:pb-0 lg:pt-0"
              >
                <h1 className="mb-5 text-left text-[3.8rem] leading-[0.9] text-primary min-[375px]:text-[4.2rem] sm:text-6xl lg:mb-6 lg:text-7xl">
                  <span className="block whitespace-nowrap font-rocky not-italic">Consultoría en</span>
                  <em className="block whitespace-nowrap font-rocky font-normal italic normal-case">sueño infantil</em>
                </h1>
                <p className="mb-6 max-w-md text-left text-lg font-bold leading-snug text-[#2D120C]/85 md:text-xl lg:mx-0 lg:mb-10 lg:max-w-none lg:text-left lg:text-2xl lg:font-medium lg:leading-relaxed lg:text-foreground/80">
                  Te acompañamos a transformar el descanso de tu bebé con una metodología única, respaldada por años de experiencia.
                </p>

                <div className="mb-8 lg:mb-10">
                  <div className="-mx-3 grid grid-cols-3 gap-2 lg:hidden">
                    {heroStats.map((stat) => (
                      <div key={stat.number} className="min-w-0 rounded-lg border border-primary bg-white/60 px-2.5 py-3 text-left">
                        <div className="max-w-full font-rocky-extra-light text-[2.1rem] italic leading-none text-primary min-[390px]:text-[2.45rem]">
                          {stat.number}
                        </div>
                        <p className="mt-2 max-w-full break-words text-[0.69rem] font-normal leading-[1.18] text-[#19110f] min-[390px]:text-[0.78rem]">
                          {stat.label}
                        </p>
                      </div>
                    ))}
                  </div>
                  <div className="hidden w-fit grid-cols-[max-content_max-content_max-content] gap-12 text-left lg:grid">
                    <StatCard number="+25.000" label={"Familias confiaron\nen mis cursos online"} delay={0.3} />
                    <StatCard number="+12.000" label={"En nuestras consultorías\npersonalizadas"} delay={0.4} />
                    <StatCard number="Desde 2019" label={"Transformando el descanso\nde las familias"} delay={0.5} />
                  </div>
                </div>

                <div className="flex flex-row gap-3 justify-start lg:gap-6">
                  <Link to="/consultorias" className="btn-base flex-1 whitespace-nowrap bg-primary px-3 text-center text-lg text-white sm:w-auto sm:flex-none sm:px-6 sm:text-xl">
                    Ver consultorías
                  </Link>
                  <Link to="/cursos" className="btn-base flex-1 whitespace-nowrap bg-accent px-3 text-center text-lg text-primary sm:w-auto sm:flex-none sm:px-6 sm:text-xl">
                    Explorar cursos
                  </Link>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        <PodcastHomeSection />

        {/* About Section */}
        <section className="section-padding bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <motion.div 
                initial={{ opacity: 0, x: -30 }} 
                whileInView={{ opacity: 1, x: 0 }} 
                viewport={{ once: true }} 
                transition={{ duration: 0.6 }}
              >
                <h2 className="mb-6 text-5xl">
                  <span className="block">Hola,</span>
                  <em className="font-rocky-light block font-light italic normal-case">Soy Lu Pistiner</em>
                </h2>
                <p className="mb-8 font-rocky text-2xl tracking-wide text-primary/80">
                  Consultora con doble certificación en sueño infantil y mamá de 2.
                </p>
                <div className="mb-10 space-y-6 text-lg leading-relaxed text-foreground/80">
                  <p>
                    Conozco lo que es no dormir: lo viví en 2014 con mi primera hija.
                    Esa experiencia fue el punto de partida que me llevó a formarme y dedicarme a ayudar a otras familias.
                  </p>
                  <p>
                    Desde 2019, acompaño a madres y padres compartiendo información real, práctica y accesible sobre el descanso infantil, un tema muy poco hablado hasta hace poco.
                  </p>
                  <p>
                    Creo profundamente en los beneficios de una buena cultura del sueño: dormir bien impacta en el desarrollo físico, cognitivo y emocional de los niños… y también en nuestra capacidad de estar presentes como madres y padres.
                  </p>
                </div>
                <Link to="/nosotras" className="btn-base bg-accent text-xl text-primary">
                  Conocé más sobre mí y mi equipo
                </Link>
              </motion.div>

              <motion.div 
                initial={{ opacity: 0, x: 30 }} 
                whileInView={{ opacity: 1, x: 0 }} 
                viewport={{ once: true }} 
                transition={{ duration: 0.6, delay: 0.2 }} 
                className="flex justify-center lg:justify-end"
              >
                <img 
                  src="/FOTOS%20LUCILA%20WEB/LUCILA-HOME-2.jpg" 
                  alt="Lucila Pistiner" 
                  loading="lazy" 
                  decoding="async"
                  className="h-80 w-full max-w-sm rounded-xl object-cover shadow-lg md:h-[460px] md:max-w-md lg:h-[560px]" 
                />
              </motion.div>
            </div>
          </div>
        </section>

        {/* Testimonials Sections */}
        <TestimonialsSection />

        {/* Courses Section */}
        <section className="section-padding bg-background">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div 
              initial={{ opacity: 0, y: 30 }} 
              whileInView={{ opacity: 1, y: 0 }} 
              viewport={{ once: true }} 
              transition={{ duration: 0.6 }} 
              className="text-center mb-20"
            >
              <h2 className="mb-6 text-5xl">Los cursos online más elegidos</h2>
              <p className="font-sans mx-auto text-base md:text-lg font-bold text-primary tracking-[0.16em] text-center uppercase">
                Herramientas prácticas, accesibles y disponibles al instante
              </p>
            </motion.div>

            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3 lg:gap-10">
              {featuredCourses.map((course, index) => (
                <CourseCard
                  key={course.link}
                  {...course}
                  delay={index * 0.1}
                />
              ))}
            </div>
            <div className="mt-12 text-center">
              <Link to="/cursos" className="btn-base bg-primary text-primary-foreground text-xl">
                Ver cursos
              </Link>
            </div>
          </div>
        </section>

        <SocialHomeSection />

        {/* Empathy Manifesto Section */}
        <section
          className="section-padding relative overflow-hidden bg-primary bg-cover bg-center text-primary-foreground"
          style={{ backgroundImage: "url('/home-maternar%20sin%20dormir.jpg')" }}
        >
          <div className="absolute inset-0 bg-primary/75" />
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <motion.div 
              initial={{ opacity: 0, y: 30 }} 
              whileInView={{ opacity: 1, y: 0 }} 
              viewport={{ once: true }} 
              transition={{ duration: 0.6 }} 
              className="text-center"
            >
              <h2 className="text-white mb-10 text-5xl md:text-6xl">
                Sabemos que maternar sin dormir puede ser muy difícil
              </h2>
              <p className="mx-auto text-lg md:text-2xl leading-relaxed text-white/90">
                Lo viví en carne propia y lo veo todos los días en las familias que acompañamos. La falta de sueño vuelve todo más cuesta arriba: las rutinas, el vínculo, la paciencia, el disfrute. Por eso, junto a mi equipo, creamos recursos y estrategias claras, prácticas y respetuosas, para ayudarte a acompañar el sueño de tu hijo en cada etapa, sin dejar de lado el apego ni tus valores. Estamos acá para decirte que no estás sola, y que sí: es posible volver a descansar.
              </p>
              <div className="mt-10 flex flex-col justify-center gap-5 sm:flex-row">
                <Link to="/consultorias" className="btn-base bg-white text-primary text-xl hover:bg-accent">
                  Ver consultorías personalizadas
                </Link>
                <Link to="/cursos" className="btn-base bg-accent text-primary text-xl hover:bg-white">
                  Explorar cursos online
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        <NewsletterSection />

      </main>

      <Footer />
    </>
  );
}

export default HomePage;
